/**
 * HLSProvider - HLS.js integration for video playback
 * Based on Vidstack's HLSController pattern
 */

/**
 * HLSProvider class - manages HLS.js instance
 */
function HLSProvider(video, playerState) {
    this._video = video;
    this._playerState = playerState;
    this._hls = null;
    this._callbacks = [];
    this._destroyed = false;
    this._lastLiveWindowSeconds = null;

    // Default HLS.js configuration for legacy devices
    this.config = {
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 30,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        fragLoadingMaxRetry: 6,
        manifestLoadingMaxRetry: 4,
        levelLoadingMaxRetry: 4,
        startLevel: -1,  // Auto quality
        // Legacy device optimizations
        // Enable native text tracks for external VTT subtitles
        renderTextTracksNatively: true,
        capLevelToPlayerSize: true,
    };
}

var LIVE_TARGET_DURATION_SECONDS = 1;
var LIVE_PLAYLIST_REFETCH_INTERVAL_MS = 1000;

function isFinitePositiveNumber(value) {
    return typeof value === 'number' && isFinite(value) && value > 0;
}

function getLiveSegmentDuration(details) {
    if (!details) return LIVE_TARGET_DURATION_SECONDS;

    if (isFinitePositiveNumber(details.targetduration)) {
        return details.targetduration;
    }
    if (isFinitePositiveNumber(details.levelTargetDuration)) {
        return details.levelTargetDuration;
    }

    var fragments = details.fragments || [];
    for (var i = 0; i < fragments.length; i++) {
        if (fragments[i] && isFinitePositiveNumber(fragments[i].duration)) {
            return fragments[i].duration;
        }
    }

    return LIVE_TARGET_DURATION_SECONDS;
}

function getLivePlaylistWindowSeconds(details) {
    if (!details || !details.live) return 0;

    if (isFinitePositiveNumber(details.totalduration)) {
        return details.totalduration;
    }

    var fragments = details.fragments || [];
    var totalDuration = 0;
    for (var i = 0; i < fragments.length; i++) {
        if (fragments[i] && isFinitePositiveNumber(fragments[i].duration)) {
            totalDuration += fragments[i].duration;
        }
    }

    if (totalDuration > 0) {
        return totalDuration;
    }

    var targetDuration = getLiveSegmentDuration(details);
    if (fragments.length > 0 && targetDuration > 0) {
        return fragments.length * targetDuration;
    }

    if (
        typeof details.startSN === 'number' &&
        typeof details.endSN === 'number' &&
        details.endSN >= details.startSN &&
        targetDuration > 0
    ) {
        return (details.endSN - details.startSN + 1) * targetDuration;
    }

    return 0;
}

function getLivePlaylistWindowSecondsFromText(text) {
    if (typeof text !== 'string') return 0;

    var lines = text.split(/\r?\n/);
    var totalDuration = 0;
    for (var i = 0; i < lines.length; i++) {
        var line = lines[i].trim();
        if (line.indexOf('#EXTINF:') === 0) {
            var duration = parseFloat(line.substring(8));
            if (isFinitePositiveNumber(duration)) {
                totalDuration += duration;
            }
        }
    }

    return totalDuration;
}

function isLiveMediaPlaylistText(text) {
    return (
        typeof text === 'string' &&
        text.indexOf('#EXTM3U') === 0 &&
        text.indexOf('#EXTINF:') !== -1 &&
        text.indexOf('#EXT-X-ENDLIST') === -1 &&
        text.indexOf('#EXT-X-STREAM-INF') === -1
    );
}

function injectLiveStartOffset(text) {
    if (!isLiveMediaPlaylistText(text)) return text;

    var liveWindowSeconds = getLivePlaylistWindowSecondsFromText(text);
    if (!isFinitePositiveNumber(liveWindowSeconds)) return text;

    var lines = text.split(/\r?\n/);
    var adjustedLines = [];
    var inserted = false;
    var startLine = '#EXT-X-START:TIME-OFFSET=-' + liveWindowSeconds.toFixed(3) + ',PRECISE=YES';

    for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        if (line.indexOf('#EXT-X-START') === 0) {
            continue;
        }

        adjustedLines.push(line);
        if (!inserted && line.indexOf('#EXTM3U') === 0) {
            adjustedLines.push(startLine);
            inserted = true;
        }
    }

    if (!inserted) {
        adjustedLines.unshift(startLine);
    }

    return adjustedLines.join('\n');
}

function createLivePlaylistLoader(Hls) {
    var BaseLoader = Hls && Hls.DefaultConfig && Hls.DefaultConfig.loader;
    var lastMediaPlaylistLoadAt = {};

    if (!BaseLoader) return null;

    function LivePlaylistLoader(config) {
        this._loader = new BaseLoader(config);
        this._loadTimer = null;
        this._pendingLoad = null;
        this.context = null;
        this.stats = this._loader.stats;
    }

    LivePlaylistLoader.prototype.load = function (context, config, callbacks) {
        var self = this;
        var isMediaPlaylist = context && context.type && context.type !== 'manifest';
        var now = performance.now();
        var key = isMediaPlaylist ? context.type + ':' + context.url : '';
        var lastLoadAt = key ? (lastMediaPlaylistLoadAt[key] || 0) : 0;
        var waitMs = key ? Math.max(0, LIVE_PLAYLIST_REFETCH_INTERVAL_MS - (now - lastLoadAt)) : 0;

        self.context = context;
        self._pendingLoad = {
            context: context,
            config: config,
            callbacks: callbacks,
            key: key,
        };

        var startLoad = function () {
            if (!self._pendingLoad) return;

            var pending = self._pendingLoad;
            self._pendingLoad = null;
            self._loadTimer = null;

            if (pending.key) {
                lastMediaPlaylistLoadAt[pending.key] = performance.now();
            }

            var callbacks = pending.callbacks;
            if (callbacks && callbacks.onSuccess) {
                callbacks = Object.assign({}, callbacks, {
                    onSuccess: function (response, stats, context, networkDetails) {
                        var nextResponse = response;
                        if (response && typeof response.data === 'string') {
                            var adjustedText = injectLiveStartOffset(response.data);
                            if (adjustedText !== response.data) {
                                nextResponse = Object.assign({}, response, { data: adjustedText });
                            }
                        }

                        pending.callbacks.onSuccess(nextResponse, stats, context, networkDetails);
                    },
                });
            }

            self._loader.load(pending.context, pending.config, callbacks);
            self.stats = self._loader.stats;
        };

        if (waitMs > 0) {
            self._loadTimer = setTimeout(startLoad, waitMs);
        } else {
            startLoad();
        }
    };

    LivePlaylistLoader.prototype.abort = function () {
        if (this._loadTimer) {
            clearTimeout(this._loadTimer);
            this._loadTimer = null;
        }
        this._pendingLoad = null;
        this._loader.abort();
    };

    LivePlaylistLoader.prototype.destroy = function () {
        if (this._loadTimer) {
            clearTimeout(this._loadTimer);
            this._loadTimer = null;
        }
        this._pendingLoad = null;
        this._loader.destroy();
    };

    LivePlaylistLoader.prototype.getCacheAge = function () {
        return this._loader.getCacheAge ? this._loader.getCacheAge() : null;
    };

    LivePlaylistLoader.prototype.getResponseHeader = function (name) {
        return this._loader.getResponseHeader ? this._loader.getResponseHeader(name) : null;
    };

    return LivePlaylistLoader;
}

function getLiveHlsConfig(Hls) {
    var liveConfig = {
        lowLatencyMode: false,
        liveSyncMode: 'buffered',
        initialLiveManifestSize: 1,
        maxLiveSyncPlaybackRate: 1.05,
    };
    var livePlaylistLoader = createLivePlaylistLoader(Hls);

    if (livePlaylistLoader) {
        liveConfig.pLoader = livePlaylistLoader;
    }

    return liveConfig;
}

/**
 * Get HLS instance
 */
HLSProvider.prototype.getInstance = function () {
    return this._hls;
};

/**
 * Check if HLS.js is supported
 */
HLSProvider.isSupported = function () {
    var Hls = window.Hls;
    return Hls && Hls.isSupported();
};

/**
 * Check if native HLS is supported
 */
HLSProvider.isNativeHLSSupported = function () {
    var video = document.createElement('video');
    return Boolean(
        video.canPlayType('application/vnd.apple.mpegurl') ||
        video.canPlayType('application/x-mpegURL')
    );
};

/**
 * Setup HLS.js instance
 */
HLSProvider.prototype.setup = function (customConfig) {
    var self = this;
    var Hls = window.Hls;

    if (!Hls) {
        console.error('[HLSProvider] HLS.js not loaded');
        return false;
    }

    if (!Hls.isSupported()) {
        console.warn('[HLSProvider] HLS.js not supported, trying native');
        return false;
    }

    // Merge custom config
    var custom = customConfig || {};
    var config = Object.assign({}, self.config, custom.isLive ? getLiveHlsConfig(Hls) : {}, custom);
    delete config.isLive;

    console.log('[HLSProvider] Creating HLS instance with config:', config);
    self._hls = new Hls(config);

    // Attach to video element
    self._hls.attachMedia(self._video);

    // Setup event handlers
    self._setupEventHandlers();

    // Notify callbacks
    for (var i = 0; i < self._callbacks.length; i++) {
        try {
            self._callbacks[i](self._hls);
        } catch (e) {
            console.error('[HLSProvider] Callback error:', e);
        }
    }

    return true;
};

/**
 * Setup HLS.js event handlers
 */
HLSProvider.prototype._setupEventHandlers = function () {
    var self = this;
    var Hls = window.Hls;

    if (!self._hls) return;

    // Manifest parsed - extract quality levels and audio tracks
    self._hls.on(Hls.Events.MANIFEST_PARSED, function (event, data) {
        console.log('[HLSProvider] MANIFEST_PARSED:', data.levels.length, 'levels');

        // Extract quality levels
        var qualities = [{ index: -1, label: 'خودکار', height: 0, bitrate: 0 }];
        for (var i = 0; i < data.levels.length; i++) {
            var level = data.levels[i];
            qualities.push({
                index: i,
                height: level.height,
                width: level.width,
                bitrate: level.bitrate,
                label: level.height + 'p',
            });
        }
        self._playerState.set('qualities', qualities);

        // Extract audio tracks
        if (data.audioTracks && data.audioTracks.length > 0) {
            var audioTracks = [];
            for (var j = 0; j < data.audioTracks.length; j++) {
                var track = data.audioTracks[j];
                audioTracks.push({
                    index: j,
                    label: track.name || track.lang || 'Track ' + (j + 1),
                    language: track.lang || '',
                });
            }
            self._playerState.set('audioTracks', audioTracks);
        }

        self._playerState.set('canPlay', true);
        self._playerState.set('loading', false);
    });

    // Level loaded - get duration and stream type
    self._hls.on(Hls.Events.LEVEL_LOADED, function (event, data) {
        var details = data.details;
        if (details) {
            if (details.live) {
                self._applyLivePlaylistWindow(details);
            }

            self._playerState.set('duration', details.totalduration || 0);
            self._playerState.set('live', details.live || false);
            self._playerState.set('streamType', details.live ? 'live' : 'on-demand');
        }
    });

    // Level switched - quality changed
    self._hls.on(Hls.Events.LEVEL_SWITCHED, function (event, data) {
        console.log('[HLSProvider] LEVEL_SWITCHED:', data.level);
        self._playerState.set('currentQuality', data.level);
    });

    // Audio track switched
    self._hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, function (event, data) {
        console.log('[HLSProvider] AUDIO_TRACK_SWITCHED:', data.id);
        self._playerState.set('currentAudioTrack', data.id);
    });

    // Fragment buffered - update buffer progress
    self._hls.on(Hls.Events.FRAG_BUFFERED, function (event, data) {
        if (self._video.buffered.length > 0) {
            var bufferedEnd = self._video.buffered.end(self._video.buffered.length - 1);
            self._playerState.set('bufferedEnd', bufferedEnd);
            self._playerState.set('buffered', bufferedEnd);
        }
    });

    // Error handling
    self._hls.on(Hls.Events.ERROR, function (event, data) {
        console.error('[HLSProvider] ERROR:', data.type, data.details);

        if (data.fatal) {
            switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                    console.log('[HLSProvider] Fatal network error, attempting recovery...');
                    self._hls.startLoad();
                    break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                    console.log('[HLSProvider] Fatal media error, attempting recovery...');
                    self._hls.recoverMediaError();
                    break;
                default:
                    console.error('[HLSProvider] Unrecoverable error');
                    self._playerState.set('error', {
                        type: data.type,
                        details: data.details,
                        message: data.error ? data.error.message : 'Unknown error',
                    });
                    break;
            }
        }
    });
};

HLSProvider.prototype._applyLivePlaylistWindow = function (details) {
    if (!this._hls || !details || !details.live) return;

    var liveWindowSeconds = getLivePlaylistWindowSeconds(details);
    if (!isFinitePositiveNumber(liveWindowSeconds)) return;

    var segmentDuration = getLiveSegmentDuration(details);
    var maxLatencySeconds = liveWindowSeconds + segmentDuration;

    this._hls.config.liveSyncDuration = liveWindowSeconds;
    this._hls.config.liveMaxLatencyDuration = maxLatencySeconds;
    this._hls.config.maxBufferLength = Math.max(segmentDuration, liveWindowSeconds);
    this._hls.config.maxMaxBufferLength = Math.max(maxLatencySeconds, liveWindowSeconds);
    this._hls.config.backBufferLength = Math.max(maxLatencySeconds, segmentDuration);

    try {
        this._hls.targetLatency = liveWindowSeconds;
    } catch (e) {
        console.warn('[HLSProvider] Could not update live target latency:', e.message);
    }

    if (this._lastLiveWindowSeconds !== liveWindowSeconds) {
        this._lastLiveWindowSeconds = liveWindowSeconds;
        console.log('[HLSProvider] Live playlist window applied:', {
            liveWindowSeconds: liveWindowSeconds,
            segmentDuration: segmentDuration,
            maxLatencySeconds: maxLatencySeconds,
        });
    }
};

/**
 * Load a source
 */
HLSProvider.prototype.loadSource = function (src) {
    if (!this._hls) {
        console.error('[HLSProvider] HLS not initialized');
        return false;
    }

    console.log('[HLSProvider] Loading source:', src);
    this._playerState.set('source', src);
    this._playerState.set('loading', true);
    this._playerState.set('error', null);

    this._hls.loadSource(src);
    return true;
};

/**
 * Set quality level
 */
HLSProvider.prototype.setQuality = function (levelIndex) {
    if (this._hls) {
        this._hls.currentLevel = levelIndex;
    }
};

/**
 * Set audio track
 */
HLSProvider.prototype.setAudioTrack = function (trackIndex) {
    if (this._hls) {
        this._hls.audioTrack = trackIndex;
    }
};

/**
 * Register callback for when HLS instance is ready
 */
HLSProvider.prototype.onInstance = function (callback) {
    var self = this;
    self._callbacks.push(callback);

    // If already setup, call immediately
    if (self._hls) {
        try {
            callback(self._hls);
        } catch (e) {
            console.error('[HLSProvider] onInstance callback error:', e);
        }
    }

    // Return unsubscribe
    return function () {
        var index = self._callbacks.indexOf(callback);
        if (index > -1) {
            self._callbacks.splice(index, 1);
        }
    };
};

/**
 * Destroy HLS instance
 */
HLSProvider.prototype.destroy = function () {
    if (this._destroyed) return;
    this._destroyed = true;

    if (this._hls) {
        console.log('[HLSProvider] Destroying HLS instance');
        this._hls.destroy();
        this._hls = null;
    }

    this._callbacks = [];
};

export { HLSProvider };
